<template>
    <div class="receipt-card">
        <div class="receipt-header">
            <div class="receipt-icon">
                <v-icon color="white">mdi-check</v-icon>
            </div>
            <h3 class="receipt-title">{{ title }}</h3>
            <p class="receipt-subtitle">{{ subtitle }}</p>
        </div>

        <div class="receipt-body">
            <div class="receipt-line-items">
                <div
                    v-for="(item, index) in items"
                    :key="index"
                    class="receipt-line-item"
                >
                    <span v-if="item.quantity" class="item-quantity">{{ item.quantity }}x</span>
                    <span class="item-description">{{ item.name }}</span>
                    <span class="item-price">{{ item.price }}</span>
                </div>
            </div>

            <div v-if="show_totals" class="receipt-totals">
                <div v-if="subtotal" class="receipt-row">
                    <span>Subtotal</span>
                    <span>{{ subtotal }}</span>
                </div>
                <div v-if="tax" class="receipt-row">
                    <span>Tax</span>
                    <span>{{ tax }}</span>
                </div>
                <div v-if="shipping" class="receipt-row">
                    <span>Shipping</span>
                    <span>{{ shipping }}</span>
                </div>
                <div class="receipt-row total-row">
                    <span>Total</span>
                    <span>{{ total }}</span>
                </div>
            </div>

            <div v-if="order_number" class="receipt-order-number">
                Order #{{ order_number }}
            </div>
        </div>

        <div v-if="show_actions" class="receipt-actions">
            <slot name="actions">
                <button
                    v-for="(action, index) in default_actions"
                    :key="index"
                    class="receipt-action-btn"
                    :class="{ 'is-primary': action.primary }"
                    @click="$emit('action', action.id)"
                >
                    <v-icon v-if="action.icon" small>{{ action.icon }}</v-icon>
                    <span>{{ action.label }}</span>
                </button>
            </slot>
        </div>
    </div>
</template>

<script>
export default {
    name: 'MockReceiptCard',

    props: {
        title: {
            type: String,
            default: 'Order Confirmed!'
        },
        subtitle: {
            type: String,
            default: 'Thank you for your order'
        },
        items: {
            type: Array,
            default: () => []
        },
        subtotal: {
            type: String,
            default: ''
        },
        tax: {
            type: String,
            default: ''
        },
        shipping: {
            type: String,
            default: ''
        },
        total: {
            type: String,
            default: ''
        },
        order_number: {
            type: String,
            default: ''
        },
        show_actions: {
            type: Boolean,
            default: true
        },
        actions: {
            type: Array,
            default: null
        }
    },

    computed: {
        show_totals() {
            return this.subtotal || this.tax || this.shipping || this.total;
        },

        default_actions() {
            if (this.actions) return this.actions;

            return [
                { id: 'text', label: 'Text Receipt', icon: 'mdi-message-text-outline' },
                { id: 'email', label: 'Email Receipt', icon: 'mdi-email-outline' },
                { id: 'download', label: 'Download PDF', icon: 'mdi-download' },
                { id: 'wallet', label: 'Add to Wallet', icon: 'mdi-wallet-outline' },
                { id: 'dismiss', label: "I don't need one", icon: null }
            ];
        }
    },
};
</script>

<style lang="scss" scoped>
.receipt {
    &-card {
        background: white;
        border-radius: 1rem;
        border-bottom-left-radius: 0.25rem;
        overflow: hidden;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        transition: all 0.2s ease;
    }

    &-header {
        background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%);
        color: white;
        padding: 1.5rem;
        text-align: center;
    }

    &-title {
        font-size: 1.25rem;
        font-weight: 600;
        margin: 0 0 0.25rem 0;
    }

    &-subtitle {
        font-size: 0.85rem;
        opacity: 0.9;
        margin: 0;
    }

    &-body {
        padding: 1.25rem;
    }

    &-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 3rem;
        height: 3rem;
        background: rgba(255, 255, 255, 0.2);
        border-radius: 50%;
        margin-bottom: 0.75rem;
    }

    &-row {
        display: flex;
        justify-content: space-between;
        padding: 0.25rem 0;
        font-size: 0.85rem;
        color: #666;

        &.total-row {
            border-top: 1px solid #e0e0e0;
            margin-top: 0.5rem;
            padding-top: 0.75rem;
            font-size: 1rem;
            font-weight: 600;
            color: #333;
        }
    }

    &-line-items {
        border-bottom: 1px dashed #e0e0e0;
        padding-bottom: 1rem;
        margin-bottom: 0.75rem;
    }

    &-line-item {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 0.5rem;
        padding: 0.35rem 0;
        font-size: 0.9rem;
    }

    &-totals {
        margin-bottom: 0.75rem;
    }

    &-order-number {
        text-align: center;
        font-size: 0.8rem;
        color: #999;
    }

    &-actions {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        padding: 1rem 1.25rem 1.25rem;
        border-top: 1px solid #f0f0f0;
    }

    &-action-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        padding: 0.75rem 1rem;
        background: #f5f5f5;
        border: none;
        border-radius: 0.5rem;
        color: #333;
        font-size: 0.85rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
            background: #e8e8e8;
        }

        &:active {
            transform: scale(0.98);
        }

        &.is-primary {
            background: #5d4037;
            color: white;

            &:hover {
                background: #4e342e;
            }
        }
    }
}

.item {
    &-description {
        color: #333;
    }

    &-quantity {
        color: #666;
        margin-right: 0.25rem;
    }

    &-price {
        color: #333;
        font-weight: 500;
    }
}
</style>
